import React from "react";
import Skeleton from "../common/skeleton";
import EmptyState from "../common/empty_state";
import SectionError from "./section_error";

const formatQty = (n) => Number(n || 0).toFixed(6);
const formatUsd = (n) =>
    "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatKrw = (n) => "₩" + Math.round(Number(n) || 0).toLocaleString();
const formatPct = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
};

// 증권사 코드 → 한국어 라벨. go-server/internal/model/stock_merge.go의 BrokerLabel과 짝을 이룬다 (DAS-169 m5).
// holdings 응답의 brokers는 []string 코드 배열이라 라벨을 싣지 않으므로 프론트에서 별도 유지한다.
// ⚠️ 새 증권사를 추가할 때는 BrokerLabel과 이 상수를 함께 고친다.
const BROKER_LABEL = { kiwoom: "키움증권", mirae: "미래에셋증권", kiwoom_isa: "키움 ISA" };

// KRW 행(키움 ISA 국내상장 ETF, DAS-170) 여부. 가격·수량 포맷이 USD 행과 다르다.
const isKrwRow = (h) => h.currency === "KRW";

// 행 통화에 맞는 단가 표시. KRW 행은 *_krw 단가(정수 원화), USD 행은 기존 *_usd.
const formatRowPrice = (h, usdValue, krwValue) =>
    isKrwRow(h) ? formatKrw(krwValue) : formatUsd(usdValue);

// KRX ETF는 정수 수량이라 소수 6자리가 노이즈다. KRW 행만 천 단위 정수로 표시.
const formatRowQty = (h) =>
    isKrwRow(h) ? Math.round(Number(h.quantity) || 0).toLocaleString() : formatQty(h.quantity);

// 계좌/증권사 배지 — accounts[]가 있으면 계좌 단위(같은 증권사 계좌 2개면 배지 2개)로,
// 없는 구 백엔드 응답은 brokers[](증권사 코드 배열)로 폴백한다 (DAS-171 P1 E-3).
function AccountTags({ accounts, brokers, stale }) {
    const acctList = Array.isArray(accounts) ? accounts : [];
    const brokerList = Array.isArray(brokers) ? brokers : [];
    if (acctList.length === 0 && brokerList.length === 0 && !stale) return null;
    return (
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
            {acctList.length > 0
                ? acctList.map((a) => (
                    <span key={a.account_id} className="rounded bg-surface-elevated px-1.5 py-0.5 text-content-muted">
                        {a.label || BROKER_LABEL[a.broker] || a.broker}
                    </span>
                ))
                : brokerList.map((b) => (
                    <span key={b} className="rounded bg-surface-elevated px-1.5 py-0.5 text-content-muted">
                        {BROKER_LABEL[b] || b}
                    </span>
                ))}
            {stale && (
                <span
                    className="rounded bg-warning-subtle px-1.5 py-0.5 text-warning"
                    title="현재 시세를 가져오지 못해 평단으로 표시 중입니다."
                >
                    시세 미조회
                </span>
            )}
        </div>
    );
}

function WeightBar({ pct }) {
    const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
    return (
        <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-elevated">
                <div className="h-full rounded-full bg-brand" style={{ width: `${clamped}%` }} />
            </div>
            <span className="font-num text-xs text-content-muted">{clamped.toFixed(1)}%</span>
        </div>
    );
}

/**
 * HoldingsTable — ⑤ 보유 종목. 데스크톱 테이블 / 모바일 카드 전환 (Tailwind sm: 브레이크포인트).
 */
function HoldingsTable({ holdings, loading, error, onRetry }) {
    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <Skeleton variant="line" width="30%" height={14} />
                <Skeleton variant="rect" height={140} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <SectionError message="보유 종목을 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const rows = Array.isArray(holdings) ? holdings : [];

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <div className="text-sm font-semibold text-content-secondary">보유 종목</div>

            {rows.length === 0 ? (
                <EmptyState
                    title="보유 중인 종목이 없습니다."
                    description="매수 기록이 확정되면 이곳에 표시됩니다."
                />
            ) : (
                <>
                    {/* 데스크톱 테이블 */}
                    <div className="mt-3 hidden overflow-x-auto sm:block">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-surface-border text-content-muted">
                                    <th className="px-2 py-2 text-left font-medium">종목</th>
                                    <th className="px-2 py-2 text-right font-medium">보유수량</th>
                                    <th className="px-2 py-2 text-right font-medium">평단</th>
                                    <th className="px-2 py-2 text-right font-medium">현재가</th>
                                    <th className="px-2 py-2 text-right font-medium">평가액(KRW)</th>
                                    <th className="px-2 py-2 text-right font-medium">손익률</th>
                                    <th className="px-2 py-2 text-right font-medium">비중</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((h) => {
                                    const pnlPositive = Number(h.pnl_pct) >= 0;
                                    return (
                                        <tr key={h.ticker} className="border-b border-surface-border-light">
                                            <td className="px-2 py-2">
                                                <div className="font-num font-semibold text-content">{h.ticker}</div>
                                                <div className="truncate text-content-muted">{h.name}</div>
                                                <AccountTags accounts={h.accounts} brokers={h.brokers} stale={h.price_stale} />
                                            </td>
                                            <td className="px-2 py-2 text-right font-num text-content">{formatRowQty(h)}</td>
                                            <td className="px-2 py-2 text-right font-num text-content-secondary">
                                                {formatRowPrice(h, h.avg_price_usd, h.avg_price_krw)}
                                            </td>
                                            <td className="px-2 py-2 text-right font-num text-content-secondary">
                                                {formatRowPrice(h, h.current_price_usd, h.current_price_krw)}
                                            </td>
                                            <td className="px-2 py-2 text-right font-num text-content">{formatKrw(h.value_krw)}</td>
                                            <td className={`px-2 py-2 text-right font-num font-semibold ${pnlPositive ? "text-gain" : "text-loss"}`}>
                                                {formatPct(h.pnl_pct)}
                                            </td>
                                            <td className="px-2 py-2 text-right">
                                                <div className="flex justify-end">
                                                    <WeightBar pct={h.weight_pct} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* 모바일 카드 */}
                    <div className="mt-3 space-y-2 sm:hidden">
                        {rows.map((h) => {
                            const pnlPositive = Number(h.pnl_pct) >= 0;
                            return (
                                <div key={h.ticker} className="rounded-xl border border-surface-border bg-surface-card-alt p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-num text-sm font-semibold text-content">{h.ticker}</div>
                                            <div className="truncate text-xs text-content-muted">{h.name}</div>
                                            <AccountTags accounts={h.accounts} brokers={h.brokers} stale={h.price_stale} />
                                        </div>
                                        <div className={`font-num text-sm font-semibold ${pnlPositive ? "text-gain" : "text-loss"}`}>
                                            {formatPct(h.pnl_pct)}
                                        </div>
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                                        <div className="text-content-muted">보유수량</div>
                                        <div className="text-right font-num text-content">{formatRowQty(h)}</div>
                                        <div className="text-content-muted">평단 / 현재가</div>
                                        <div className="text-right font-num text-content-secondary">
                                            {formatRowPrice(h, h.avg_price_usd, h.avg_price_krw)} /{" "}
                                            {formatRowPrice(h, h.current_price_usd, h.current_price_krw)}
                                        </div>
                                        <div className="text-content-muted">평가액</div>
                                        <div className="text-right font-num text-content">{formatKrw(h.value_krw)}</div>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-xs text-content-muted">비중</span>
                                        <WeightBar pct={h.weight_pct} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default HoldingsTable;
